import _get from 'lodash/get'
import _pick from 'lodash/pick'
import { match } from 'ts-pattern'
import type { ReadonlyDeep } from 'type-fest'
import { z } from 'zod'

import type {
  Condition,
  ConditionValues,
  DisabledConditionСonfig,
} from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'

type GetConditionsFromElements = (
  arg0: readonly any[],
  arg1: FlowValues
) => Record<string, number | boolean | string | undefined>

const valueSchema = z.union([z.number(), z.string(), z.boolean()])

const resolveValuePath = (flowValues: FlowValues, valuePath: string) => {
  const value = valueSchema.safeParse(_get(flowValues, valuePath))
  if (value.success) {
    return value.data
  }
  return undefined
}

export const evaluateCondition = (
  condition: ReadonlyDeep<Condition>,
  flowValues: FlowValues
): boolean => {
  return match(condition)
    .returnType<boolean>()
    .with(
      {
        type: 'value',
      },
      (cond) => {
        const value = cond.valuePath ? _get(flowValues, cond.valuePath) : cond.value
        const res = value === cond.expectedValue
        if (cond.equalityType === 'notEquals') {
          return !res
        }
        return res
      }
    )
    .with(
      {
        type: 'function',
      },
      (cond) => {
        const values = _pick(flowValues, cond.valuePaths ?? [])
        return cond.function(values)
      }
    )
    .with(
      {
        type: 'conditions',
      },
      (cond) => {
        if (cond.comparisonType === 'allOf') {
          return cond.conditions.every((c) => evaluateCondition(c, flowValues))
        }
        return cond.conditions.some((c) => evaluateCondition(c, flowValues))
      }
    )
    .exhaustive()
}

export const getDisabledConditionValuesFromElements: GetConditionsFromElements = (
  elements,
  flowValues
) => {
  const values: Record<string, string | number | boolean | undefined> = {}

  const disabledElements = elements.filter(
    (element) => 'disabledConditionConfig' in element && element.disabledConditionConfig.condition
  )

  disabledElements.forEach((element) => {
    const {
      disabledConditionConfig: { condition },
    }: {
      disabledConditionConfig: DisabledConditionСonfig
    } = element

    values[condition.valuePath] = resolveValuePath(flowValues, condition.valuePath)
  })

  return values
}

export const evaluateDisabledCondition = (
  conditions: ConditionValues,
  disabledCondition: DisabledConditionСonfig['condition']
): boolean => {
  return String(conditions[disabledCondition.valuePath]) === String(disabledCondition.expectedValue)
}
